import { CSSProperties } from 'react';
import { HeaderCellModel } from '../../types';
import { ComponentMatrixGroup } from '../../types';
import { colors } from '@ardoq/design-tokens';
import { hasChildren, isFirstChild, isLastChild } from '../../util';
const BORDER_RADIUS = '8px';

type CardStyle = {
  spanStyle: CSSProperties;
  headerStyle: CSSProperties;
};

/**
 * Returns styles for the two parts of a row header card:
 * - headerStyle: applied to CardStripe — full pill for leaf/collapsed, top-only border for expanded groups
 * - spanStyle: applied to GroupOutline — left/right/bottom border below the stripe (expanded groups only)
 */
export const getRowHeaderCardStyle = (
  headerCell: HeaderCellModel,
  fillColor: string,
  borderColorOverride?: string
): CardStyle => {
  const borderColor = borderColorOverride ?? (fillColor || colors.grey300);
  const headerBg = fillColor || colors.surfaceDefault;

  const isLeafOrCollapsed =
    !headerCell.group.isExpanded || !hasChildren(headerCell.group);

  // spanStyle: applied to GroupOutline — an absolutely-positioned overlay inset
  // from the top by CARD_GROUP_TOP_MARGIN, so borders align with the first
  // child's stripe rather than the top of the grid cell.
  const spanStyle: CSSProperties = {
    borderLeft: `1px solid ${borderColor}`,
    borderRight: `1px solid ${borderColor}`,
    borderBottom: `1px solid ${borderColor}`,
    borderBottomLeftRadius: BORDER_RADIUS,
    borderBottomRightRadius: BORDER_RADIUS,
    boxSizing: 'border-box',
  };

  // For expanded groups: stripe covers only the top (ROW_HEADER_STRIPE_HEIGHT);
  // GroupOutline draws the left/right/bottom border of the body below.
  // No bottom border on the stripe — GroupOutline's top aligns with stripe bottom.
  const headerStyle: CSSProperties = isLeafOrCollapsed
    ? {
        backgroundColor: headerBg,
        border: `1px solid ${borderColor}`,
        borderRadius: BORDER_RADIUS,
        boxSizing: 'border-box',
      }
    : {
        backgroundColor: headerBg,
        borderTop: `1px solid ${borderColor}`,
        borderLeft: `1px solid ${borderColor}`,
        borderRight: `1px solid ${borderColor}`,
        borderTopLeftRadius: BORDER_RADIUS,
        borderTopRightRadius: BORDER_RADIUS,
        boxSizing: 'border-box',
      };

  return {
    spanStyle,
    headerStyle,
  };
};

/**
 * Styles for list-value (non-component) row header cells.
 */
const getRowHeaderCellStyle = (
  headerCell: HeaderCellModel,
  fillColor: string
): CSSProperties => {
  const level = headerCell.indexAddress.length - 1;
  const borderColor = fillColor || colors.grey300;
  const backgroundColor = fillColor || 'transparent';

  if (level === 0) {
    return {
      borderBottom: `1px solid ${borderColor}`,
      backgroundColor,
    };
  }

  const firstChild = isFirstChild(headerCell);
  const lastChild = isLastChild(headerCell);

  const style: CSSProperties = {
    borderLeft: `1px solid ${borderColor}`,
    borderRight: `1px solid ${borderColor}`,
    backgroundColor,
    height: '100%',
    boxSizing: 'border-box',
  };

  if (firstChild) {
    style.borderTopLeftRadius = BORDER_RADIUS;
    style.borderTopRightRadius = BORDER_RADIUS;
  }
  if (lastChild) {
    style.borderBottom = `1px solid ${borderColor}`;
    style.borderBottomLeftRadius = BORDER_RADIUS;
    style.borderBottomRightRadius = BORDER_RADIUS;
  }

  return style;
};

type ColumnCardStyle = {
  /** Applied to the top stripe of the column header card. */
  stripeStyle: CSSProperties;
  /** Applied to the GroupOutline overlay (left/right/bottom group boundary). */
  spanStyle: CSSProperties;
};

/**
 * Returns styles for a component column header card:
 * - stripeStyle: top stripe (solid fill color, top-left/top-right radius,
 *   left/right/top border only — mirrors the Figma spec exactly)
 * - spanStyle: applied to an absolutely-positioned GroupOutline overlay inset
 *   from the top by COLUMN_HEADER_STRIPE_HEIGHT, covering left/right/bottom to
 *   draw the group container boundary over the children cells below.
 *   Only needed when the column is expanded with children.
 */
export const getColumnHeaderCardStyle = (
  column: ComponentMatrixGroup,
  fillColor: string,
  borderColorOverride?: string
): ColumnCardStyle => {
  const borderColor = borderColorOverride ?? (fillColor || colors.grey300);
  const stripeBg = fillColor || colors.surfaceDefault;

  const isLeafOrCollapsed = !column.isExpanded || !hasChildren(column);

  const stripeStyle: CSSProperties = {
    backgroundColor: stripeBg,
    borderTop: `1px solid ${borderColor}`,
    borderLeft: `1px solid ${borderColor}`,
    borderRight: `1px solid ${borderColor}`,
    borderTopLeftRadius: BORDER_RADIUS,
    borderTopRightRadius: BORDER_RADIUS,
    // No bottom border — group outline handles it when expanded; full radius when leaf/collapsed
    ...(isLeafOrCollapsed
      ? {
          borderBottom: `1px solid ${borderColor}`,
          borderBottomLeftRadius: BORDER_RADIUS,
          borderBottomRightRadius: BORDER_RADIUS,
        }
      : {}),
    boxSizing: 'border-box',
  };

  const spanStyle: CSSProperties = {
    borderLeft: `1px solid ${borderColor}`,
    borderRight: `1px solid ${borderColor}`,
    borderBottom: `1px solid ${borderColor}`,
    borderBottomLeftRadius: BORDER_RADIUS,
    borderBottomRightRadius: BORDER_RADIUS,
    boxSizing: 'border-box',
  };

  return { stripeStyle, spanStyle };
};

export { getRowHeaderCellStyle };
